import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { useStore } from "react-redux"
import { useTranslation } from "react-i18next"
import type { TreeItem } from "@atlaskit/tree/types"
import KeyboardNode, {
    CodeBlock,
    MarkdownNode,
    QuickActionsNode,
    StickerNode
} from "../components/ContentEditor/extensions/Keyboard/KeyboardNode"
import { Dispatch } from "../utility/common/storeHelper"
import { makeArticleContentEditorDictionary } from "../utility/articleContentEditor/dictionary"
import { getArticleEditorEmbeds } from "../utility/knowledgeBase/articleEditorEmbeds"
import { stickersApiController } from "../api/controllers/stickers"
import { Sticker } from "../models/sticker"
import { useAppSelector } from "../store/hooks"
import { selectShareCatalogProjectId } from "../store/knowledgeBase/selectors"
import { skipToken } from "@reduxjs/toolkit/dist/query"

const EMPTY_STICKERS: Sticker[] = []

export interface UseContentExtensionsParams {
    projectId?: string
    onOpenArticle?: (articleCode: string, categoryItem?: TreeItem, catalogCode?: string) => void
    onChangeLocation?: (path: string) => void
    onDispatch?: Dispatch
    channels?: string[]
    articleSymbolCode?: string
    allowedFileExtensions?: string[]
    allowedVideoExtensions?: string[]
    allowedAudioExtensions?: string[]
}

const noop = () => void 0

export const useContentExtensions = (params: UseContentExtensionsParams) => {
    const {
        projectId,
        onOpenArticle,
        onChangeLocation,
        onDispatch,
        channels,
        articleSymbolCode,
        allowedFileExtensions = [],
        allowedVideoExtensions = [],
        allowedAudioExtensions = []
    } = params

    const { t } = useTranslation()
    const store = useStore()
    const { shareId } = useParams<{ shareId?: string }>()

    const sharedProjectId = useAppSelector(selectShareCatalogProjectId)
    const selectedProjectId = shareId ? sharedProjectId : projectId

    const stickerEndpoint = shareId
        ? stickersApiController.endpoints.getSharedStickers
        : stickersApiController.endpoints.getStickers

    const { data: stickerData, isLoading: isLoadingSticker } = stickerEndpoint.useQuery(selectedProjectId ?? skipToken)
    const stickers = stickerData?.Stickers ?? EMPTY_STICKERS

    const dictionary = useMemo(() => makeArticleContentEditorDictionary(t), [t])

    const embeds = useMemo(
        () => getArticleEditorEmbeds(t, allowedFileExtensions, allowedVideoExtensions, allowedAudioExtensions),
        [t, allowedFileExtensions, allowedVideoExtensions, allowedAudioExtensions]
    )

    const extensions = useMemo(() => {
        if (projectId || shareId) {
            const safeOnOpenArticle = onOpenArticle ?? noop
            const safeOnChangeLocation = onChangeLocation ?? noop
            const safeOnDispatch = onDispatch ?? noop

            return [
                new KeyboardNode({
                    projectId,
                    onOpenArticle: safeOnOpenArticle,
                    onDispatch: safeOnDispatch,
                    t,
                    onChangeLocation: safeOnChangeLocation,
                    channels,
                    articleSymbolCode,
                    stickers,
                    store
                }),
                new CodeBlock({ projectId, onOpenArticle: safeOnOpenArticle, onDispatch: safeOnDispatch, t, store }),
                new MarkdownNode({ projectId, onOpenArticle: safeOnOpenArticle, onDispatch: safeOnDispatch, t, store }),
                new QuickActionsNode({
                    projectId,
                    onOpenArticle: safeOnOpenArticle,
                    onDispatch: safeOnDispatch,
                    t,
                    store
                }),
                new StickerNode({
                    projectId,
                    onOpenArticle: safeOnOpenArticle,
                    onDispatch: safeOnDispatch,
                    t,
                    stickers,
                    store
                })
            ]
        } else {
            return []
        }
    }, [stickers, store, projectId, t, onOpenArticle, onChangeLocation, channels, articleSymbolCode])

    return {
        extensions,
        embeds,
        dictionary,
        isLoadingSticker
    }
}
